import { useCallback } from 'react';
import { Textarea, useMantineTheme } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';
import bannerWordsRefine from '../utils/bannedWordsRefine';

export type TextAreaFieldType = BaseInterativeField & {
  type: 'TextArea';
  validation?: {
    bannedWords?: string[];
    bannedWordsHidden?: string[];
    softWords?: string[];
  };
};

function TextAreaFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
}: StaticFieldProps<TextAreaFieldType>) {
  const theme = useMantineTheme();
  const isError = errors && isTouched;

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;

      onValueChange(target.value);
    },
    [onValueChange]
  );

  return (
    <div>
      <FieldMeta field={field} errors={errors} isTouched={isTouched} />
      <Textarea
        defaultValue={typeof field?.defaultValue === 'string' ? field.defaultValue : ''}
        styles={{
          input: {
            minHeight: 48,
            borderColor: isError && theme.colors.red[6],
            borderRadius: theme.radius.md,
            '&::placeholder': {
              color: isError ? theme.colors.red[6] : 'inherit',
            },
          },
        }}
        value={typeof value === 'string' ? value : ''}
        onChange={handleOnChange}
      />
    </div>
  );
}

const createValidationSchema = (field: TextAreaFieldType) => {
  let fieldSchema: ZodTypeAny = z.union([
    z.string({ required_error: 'Field is required' }).trim(),
    z.null(),
  ]);

  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  } else {
    // @ts-expect-error Not sure how to type this
    fieldSchema = fieldSchema.min(1, { message: 'Field is required' });
  }

  if (field.validation?.bannedWords) {
    fieldSchema = fieldSchema.refine(bannerWordsRefine(field.validation.bannedWords), (value) => ({
      message: `Your ${field.label.toLowerCase()} cannot contain the following phrase: ${value}`,
    }));
  }

  if (field.validation?.bannedWordsHidden) {
    fieldSchema = fieldSchema.refine(
      bannerWordsRefine(field.validation.bannedWordsHidden, field.validation.softWords, {
        cleanValue: false,
      }),
      {
        message: `Your ${field.label.toLowerCase()} has a word or phrase that you cannot use.`,
      }
    );
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<TextAreaFieldType> = {
  FieldComponent: TextAreaFieldElement,
  name: 'TextArea',
  createValidationSchema,
};

export default Descriptor;
